import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'

import Field from 'components/Recommendations/Campaign/Edit/CampaignForm/Field'
import DropDown from 'components/Shared/DropDown'
import InfoBox from 'components/Shared/InfoBox'
import { useTranslation } from 'react-i18next'
import { hasPartialServiceAccessTier } from 'hooks/useHasPermission'

import PostalCodes from './PostalCodes'
import RangeLocations from './Range'
import { useTargeting, useTargetingDispatch } from './TargetingContext'
import {
  TARGETING_MODES,
  actions,
  activeTargetingPlatforms,
  getDefaultTargetingMode,
  getLocationTargetingModeOptions,
  isGeoTargetingEmpty,
  validateLocationTargeting,
  withCampaignWideTargetingFallback,
} from './targeting'
import { useCampaignSetup } from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import { derivePlatformSelectionFromSetup } from 'components/Recommendations/Campaign/platform'
import { hasAudienceTargeting } from 'components/Recommendations/Campaign/Context/util'

export function GeoTargetingField({
  tc,
  tc_run_id,
  disabled,
  modeLocked = false,
  onChange,
  showValidationErrors,
  savedAudienceSelected = false,
  hasTargetingLimitations = false,
  isFirstTc = false,
}) {
  const { t } = useTranslation()
  const hasPrismaAccess = hasPartialServiceAccessTier()

  const dispatch = useTargetingDispatch()
  const { value: targeting } = useTargeting()
  const tcTargeting = useMemo(() => targeting.find((x) => x.tc === tc) ?? { tc }, [targeting, tc])

  const { setup } = useCampaignSetup()
  const platforms = useMemo(
    () => activeTargetingPlatforms(derivePlatformSelectionFromSetup(setup)),
    [setup],
  )
  // Read-only fallback for a gated TC (Google Display, or Meta+Google
  // combined) — the write paths below (onModeChange/onPostalCodesChange/
  // onRangeLocationsChange) keep using the raw tcTargeting so a save never
  // re-persists campaign-wide content as this TC's own contribution; that's
  // moot in practice once the field is disabled for a gated non-first TC,
  // but keeps the two concerns cleanly separated.
  const effectiveTcTargeting = useMemo(
    () => withCampaignWideTargetingFallback(tcTargeting, setup, isFirstTc),
    [tcTargeting, setup, isFirstTc],
  )
  const hasCustomAudience = useMemo(
    () => hasAudienceTargeting(effectiveTcTargeting),
    [effectiveTcTargeting],
  )
  const options = useMemo(
    () =>
      getLocationTargetingModeOptions(hasPrismaAccess, hasCustomAudience, hasTargetingLimitations),
    [hasPrismaAccess, hasCustomAudience, hasTargetingLimitations],
  )
  const optionsWithLimitations = useMemo(
    () =>
      options.map((option) =>
        hasTargetingLimitations &&
        (option.value === TARGETING_MODES.postalCodes ||
          option.value === TARGETING_MODES.fdTargeting)
          ? { ...option, disabled: true }
          : option,
      ),
    [options, hasTargetingLimitations],
  )

  // Attempt fallback to campaign level targeting mode before falling back to tc default
  const existingTargetingMode = platforms
    .map((p) => effectiveTcTargeting[p]?.targeting_mode)
    .find(Boolean)
  const currentTargetingMode =
    existingTargetingMode ??
    getDefaultTargetingMode(hasPrismaAccess, hasCustomAudience, hasTargetingLimitations)

  // A bare `{targeting_mode}` block is stripped by stripEmptyPlatformTargeting
  // server-side, and the success handler overwrites local state with the echo.
  // Skip the write when neither payload has content — it would just come back
  // stripped and reset the dropdown ("mode flips back by itself"). Allow it
  // through when content IS changing to/from empty so the change reaches the backend.
  const shouldSkipPersist = useCallback(
    (nextPayloadByPlatform) => {
      // Geo-scoped, not isPlatformTargetingEmpty: tcTargeting[platform] can
      // carry this TC's own audience/age content regardless of geo, which is
      // irrelevant to whether this geo-only write actually changes anything.
      // hadContent reads effectiveTcTargeting, not raw tcTargeting: once a
      // gated TC's geo has round-tripped and stripped, the raw value always
      // looks empty, making a mode switch with no new content look like a
      // no-op and silently skip the write.
      const hadContent = platforms.some(
        (platform) => !isGeoTargetingEmpty(effectiveTcTargeting[platform]),
      )
      const hasContent = platforms.some(
        (platform) => !isGeoTargetingEmpty(nextPayloadByPlatform[platform]),
      )
      return !hadContent && !hasContent
    },
    [platforms, effectiveTcTargeting],
  )

  const onModeChange = useCallback(
    (mode) => {
      const payload = { tc, tc_run_id }
      for (const platform of platforms) {
        const next = { ...tcTargeting[platform], targeting_mode: mode }
        if (mode !== TARGETING_MODES.postalCodes) delete next.postal_codes
        if (mode !== TARGETING_MODES.rangeLocations) delete next.range_locations
        payload[platform] = next
      }
      dispatch({ type: actions.setTcTargeting, payload })

      // Unlike onPostalCodesChange/onRangeLocationsChange below, this can't
      // just skip onChange entirely when there's no geo content yet: the
      // targeting_mode itself changing (e.g. away from fd_targeting, which
      // validateLocationTargeting treats as always-valid, into postal_codes/
      // range_locations, which requires real content) is exactly what makes
      // this TC newly invalid — that has to reach setup.targeting (what
      // Next/Update validates against) even with nothing entered yet.
      // shouldSkipPersist still decides whether the *network* PUT fires —
      // skipping it here avoids the round-trip-strip-and-revert described
      // above; the local dispatch above already applied the mode change.
      onChange({
        targetingMode: mode,
        skipNetworkSync: shouldSkipPersist(payload),
        ...Object.fromEntries(platforms.map((p) => [p, payload[p]])),
      })
    },
    [dispatch, onChange, platforms, tcTargeting, tc, tc_run_id, shouldSkipPersist],
  )

  const onPostalCodesChange = useCallback(
    (platformPayload) => {
      dispatch({ type: actions.setTcTargeting, payload: { tc, tc_run_id, ...platformPayload } })
      if (shouldSkipPersist(platformPayload)) {
        return
      }
      onChange({ targetingMode: currentTargetingMode, ...platformPayload })
    },
    [dispatch, onChange, currentTargetingMode, tc, tc_run_id, shouldSkipPersist],
  )

  const onRangeLocationsChange = useCallback(
    (platformPayload) => {
      dispatch({ type: actions.setTcTargeting, payload: { tc, tc_run_id, ...platformPayload } })
      if (shouldSkipPersist(platformPayload)) {
        return
      }
      onChange({ targetingMode: currentTargetingMode, ...platformPayload })
    },
    [dispatch, onChange, currentTargetingMode, tc, tc_run_id, shouldSkipPersist],
  )

  useEffect(() => {
    // If targeting mode is fdTargeting (or not set, which defaults to fdTargeting) and the option is not available, set the default targeting mode
    // This happens when a custom audience is selected, which removes the fdTargeting option
    // from the dropdown.
    if (
      !modeLocked &&
      (!existingTargetingMode || existingTargetingMode === TARGETING_MODES.fdTargeting) &&
      !options.find((x) => x.value === TARGETING_MODES.fdTargeting)
    ) {
      // onModeChange itself now no-ops the network write when there's no
      // location content yet, so this can't loop even though it re-runs
      // every time its deps are recomputed with the same "still invalid"
      // result.
      onModeChange(
        getDefaultTargetingMode(hasPrismaAccess, hasCustomAudience, hasTargetingLimitations),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    existingTargetingMode,
    hasCustomAudience,
    hasPrismaAccess,
    options,
    hasTargetingLimitations,
    modeLocked,
  ])

  const validationError = useMemo(() => {
    if (!showValidationErrors) return null
    const { valid, code } = validateLocationTargeting(
      targeting,
      tc,
      tc_run_id,
      hasCustomAudience,
      savedAudienceSelected,
      platforms,
      setup,
    )
    if (valid || !code) return null

    return t(`Recommendations.geoTargeting.validationMsgs.${code}`)
  }, [
    showValidationErrors,
    targeting,
    tc,
    tc_run_id,
    t,
    hasCustomAudience,
    savedAudienceSelected,
    platforms,
    setup,
  ])

  return (
    <div className="creative-composer-section-wrapper geo-targeting-field m-0">
      <Field
        fieldName="geo_targeting"
        as={DropDown}
        value={currentTargetingMode}
        disabledText={t(`Recommendations.geoTargeting.options.${currentTargetingMode}`)}
        onChange={onModeChange}
        required={false}
        disabled={
          disabled ||
          modeLocked ||
          (savedAudienceSelected && !platforms.includes('google')) ||
          options.length === 1
        }
        options={optionsWithLimitations}
        renderItem={(item) => {
          if (!item) return null

          const label = t(item.text)

          if (hasTargetingLimitations && item.value === TARGETING_MODES.postalCodes) {
            return (
              <InfoBox
                withAnchor={false}
                withArrow={false}
                body={t('Tips.Recommendations.CreativeComposer.PostalCodesDisabled.body')}
              >
                {label}
              </InfoBox>
            )
          }

          return label
        }}
        classNames="geo-targeting-dropdown"
        inputWhenDisabled
        tooltip={{
          title: t('Tips.Recommendations.CreativeComposer.GeoTargeting.title'),
          body: (
            <div
              className="d-flex flex-column gap-2"
              style={{ fontSize: '.875rem', fontWeight: 500 }}
            >
              <p>{t('Tips.Recommendations.CreativeComposer.GeoTargeting.1')}</p>
            </div>
          ),
        }}
        inputTooltip={{
          body: t('Tips.Recommendations.CreativeComposer.GeoTargetingDisabled.body'),
          enabled: savedAudienceSelected && !platforms.includes('google'),
          withAnchor: false,
        }}
        errorMessage={validationError}
      />
      {/* If saved audience is enabled, completely block editing targeting (Edit button, postal codes, etc.) */}
      <PostalCodes
        tc={tc}
        isFirstTc={isFirstTc}
        onChange={onPostalCodesChange}
        targetingMode={currentTargetingMode}
        disabled={
          disabled ||
          (savedAudienceSelected && !platforms.includes('google')) ||
          hasTargetingLimitations
        }
      />

      <RangeLocations
        tc={tc}
        isFirstTc={isFirstTc}
        onChange={onRangeLocationsChange}
        targetingMode={currentTargetingMode}
        disabled={disabled || (savedAudienceSelected && !platforms.includes('google'))}
        hasTargetingLimitations={hasTargetingLimitations}
      />
    </div>
  )
}

GeoTargetingField.propTypes = {
  tc: PropTypes.string.isRequired,
  tc_run_id: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  modeLocked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  showValidationErrors: PropTypes.bool,
  savedAudienceSelected: PropTypes.bool,
  hasTargetingLimitations: PropTypes.bool,
  isFirstTc: PropTypes.bool,
}
